import { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useOnClickOutside, ApprovedIcon, CloseIcon } from '@instacart/ids-core'
import {
  IconButton,
  Text,
  Button,
  TDS_DEFAULT_COLORS,
  SegmentSwitch,
} from '@retailer-platform/shared-components/src/tds'
import { TextArea, borderRadius } from '@retailer-platform/shared-components'
import { useDashMessages } from '../../../../intl/intl.hooks'

const ANIMATION_DURATION_MS = 300
const MODAL_WIDTH = 411
const PADDING = 24

// Shorthand for TDS colors
const colors = TDS_DEFAULT_COLORS.colors

const slideIn = keyframes({
  from: {
    transform: 'translateX(100%)',
  },
  to: {
    transform: 'translateX(0)',
  },
})

const slideOut = keyframes({
  from: {
    opacity: 1,
    transform: 'translateX(0)',
  },
  to: {
    opacity: 0,
    transform: 'translateX(100%)',
  },
})

const ModalOverlay = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  pointerEvents: 'none',
})

interface ModalContainerProps {
  isClosing: boolean
}

const ModalContainer = styled.div<ModalContainerProps>(({ isClosing }) => ({
  position: 'absolute',
  right: PADDING,
  bottom: PADDING,
  width: MODAL_WIDTH,
  backgroundColor: colors.systemGrayscale00,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
  animation: isClosing
    ? `${slideOut} ${ANIMATION_DURATION_MS}ms ease-in-out`
    : `${slideIn} ${ANIMATION_DURATION_MS}ms ease-in-out`,
  pointerEvents: 'auto',
}))

const HeaderContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: PADDING,
})

const ContentContainer = styled.div({
  padding: PADDING,
  paddingTop: 0,
})

const RatingContainer = styled.div({
  marginTop: 12,
  marginBottom: 16,
  // Make SegmentSwitch fill the full width
  '& > div': {
    width: '100%',
  },
})

const RatingLabels = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 16,
})

const FeedbackSection = styled.div({
  marginTop: PADDING,
})

const ButtonContainer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 16,
})

const SuccessContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: 16,
  backgroundColor: colors.systemSuccessLight,
  borderRadius: borderRadius.X8,
})

const StyledTextArea = styled(TextArea)({
  marginTop: 8,
  minHeight: 80,
  borderRadius: borderRadius.X8,
})

export interface ToolSurveyModalProps {
  /** The tool name to display in the question "How satisfied are you with the [toolName] tool?" */
  toolName: string
  isOpen: boolean
  onClose: () => void
  onSubmit: (rating: number, feedback: string) => void
  isSubmitting?: boolean
  showSuccess?: boolean
  /** If set, auto-close with slide-out animation after this many ms when showSuccess becomes true */
  autoCloseAfterSuccessMs?: number
}

export const ToolSurveyModal = ({
  toolName,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  showSuccess = false,
  autoCloseAfterSuccessMs,
}: ToolSurveyModalProps) => {
  const [isClosing, setIsClosing] = useState(false)
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [feedback, setFeedback] = useState('')
  const modalContainerRef = useRef<HTMLDivElement>(null)

  const messages = useDashMessages({
    title: 'toolSurvey.title',
    question: 'toolSurvey.question',
    veryDissatisfied: 'toolSurvey.veryDissatisfied',
    verySatisfied: 'toolSurvey.verySatisfied',
    feedbackLabel: 'toolSurvey.feedbackLabel',
    feedbackPlaceholder: 'toolSurvey.feedbackPlaceholder',
    submit: 'toolSurvey.submit',
    successMessage: 'toolSurvey.successMessage',
    close: 'common.close',
  })

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      setSelectedRating(null)
      setFeedback('')
      onClose()
    }, ANIMATION_DURATION_MS)
  }, [onClose])

  // Delay enabling click-outside detection to prevent the opening click from closing the modal
  const [isClickOutsideEnabled, setIsClickOutsideEnabled] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // Wait for next frame before enabling click-outside detection
      const timeoutId = setTimeout(() => setIsClickOutsideEnabled(true), 0)
      return () => clearTimeout(timeoutId)
    } else {
      setIsClickOutsideEnabled(false)
    }
  }, [isOpen])

  useOnClickOutside(modalContainerRef, isClickOutsideEnabled ? handleClose : () => {})

  // Auto-close with slide-out animation after success
  useEffect(() => {
    if (showSuccess && autoCloseAfterSuccessMs && autoCloseAfterSuccessMs > 0) {
      const timeoutId = setTimeout(() => {
        handleClose()
      }, autoCloseAfterSuccessMs)
      return () => clearTimeout(timeoutId)
    }
  }, [showSuccess, autoCloseAfterSuccessMs, handleClose])

  const handleSubmit = useCallback(() => {
    if (selectedRating !== null) {
      onSubmit(selectedRating, feedback)
    }
  }, [selectedRating, feedback, onSubmit])

  const handleRatingChange = useCallback((value: string) => {
    setSelectedRating(parseInt(value, 10))
  }, [])

  const ratingItems = useMemo(
    () => [
      { label: '1', value: '1' },
      { label: '2', value: '2' },
      { label: '3', value: '3' },
      { label: '4', value: '4' },
      { label: '5', value: '5' },
    ],
    []
  )

  if (!isOpen) return null

  return (
    <ModalOverlay>
      <ModalContainer ref={modalContainerRef} isClosing={isClosing} data-testid="tool-survey-modal">
        <HeaderContainer>
          <Text typography="title">{messages.title}</Text>
          <IconButton
            icon={CloseIcon}
            aria-label={messages.close}
            onClick={handleClose}
            variant="secondary"
            compact
            data-testid="tool-survey-close"
          />
        </HeaderContainer>

        <ContentContainer>
          {showSuccess ? (
            <SuccessContainer data-testid="tool-survey-success">
              <ApprovedIcon color="systemSuccessRegular" size={24} />
              <Text typography="bodyRegular">{messages.successMessage}</Text>
            </SuccessContainer>
          ) : (
            <>
              <Text typography="subtitle">{messages.question.replace('{toolName}', toolName)}</Text>

              <RatingContainer data-testid="tool-survey-rating">
                <SegmentSwitch
                  items={ratingItems}
                  value={selectedRating?.toString()}
                  onChange={handleRatingChange}
                />
              </RatingContainer>

              <RatingLabels>
                <Text typography="accentRegular" color="systemGrayscale60">
                  {messages.veryDissatisfied}
                </Text>
                <Text typography="accentRegular" color="systemGrayscale60">
                  {messages.verySatisfied}
                </Text>
              </RatingLabels>

              {selectedRating !== null && (
                <FeedbackSection>
                  <Text typography="bodyRegular">{messages.feedbackLabel}</Text>
                  <StyledTextArea
                    placeholder={messages.feedbackPlaceholder}
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    data-testid="tool-survey-feedback"
                  />

                  <ButtonContainer>
                    <Button
                      variant="primary"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      data-testid="tool-survey-submit"
                    >
                      {messages.submit}
                    </Button>
                  </ButtonContainer>
                </FeedbackSection>
              )}
            </>
          )}
        </ContentContainer>
      </ModalContainer>
    </ModalOverlay>
  )
}
